<template>
  <div>
    <template v-if="loading">
      <send-loading :loading="loading" :no-quote="true"></send-loading>
    </template>
    <template v-if="!loading">
      <div
        v-for="option in filteredTags"
        :key="option.id"
      >
        <el-tag style="margin-bottom: 0.3em" :color="option.color">
          <el-checkbox
            v-model="contactTags[option.id]"
            @change="changeTags(option.id)"
          >
            <span :style="{ color: contrastTextColor(option.color) }">{{
              option.label
            }}</span>
          </el-checkbox>
        </el-tag>
      </div>
    </template>
  </div>
</template>
<script>
import { mapActions, mapGetters } from 'vuex';
import { processError } from 'src/util/processError.js';
import { contrastTextColor } from 'src/util/colorUtils';

import tagService from 'src/services/tagService.js';
import contactServices from 'src/services/contactServices';

export default {
  props: {
    profileId: {
      type: Number,
      default: () => {
        return 0;
      },
    },
  },
  data() {
    return {
      loading: true,
      contactTags: {},
      contact: undefined,
    };
  },
  computed: {
    ...mapGetters('tags', ['getTags', 'getContactTagChange']),
    ...mapGetters('auth', ['getGlobalBrandIds']),
    filteredTags() {
      const activeTags = this.getTags.filter((t) => t.active != false);
      // undefined or null means no filter, show all tags
      if (!this.getGlobalBrandIds) {
        return activeTags;
      }
      return activeTags.filter((t) => {
        // Tags with no brand_ids assigned show for all brands, as do hero tags
        if (t.is_hero != null || !t.brand_ids || t.brand_ids.length === 0) {
          return true;
        }
        return t.brand_ids.some((id) => this.getGlobalBrandIds.includes(id));
      });
    },
  },
  watch: {
    profileId(n) {
      this.onLoad();
    },
    // Tags saved elsewhere for this contact, ie hero tags or another tag card
    getContactTagChange(c) {
      if (!c || c.contactId != this.profileId || !this.contact) {
        return;
      }
      this.contact.tags = c.tags;
      const ids = new Set(c.tags.map((t) => t.id));
      for (const id of Object.keys(this.contactTags)) {
        this.contactTags[id] = ids.has(+id);
      }
      for (const id of ids) {
        this.contactTags[id] = true;
      }
    },
  },
  created() {
    this.onLoad();
  },
  methods: {
    contrastTextColor,
    publishTags(tags) {
      this.$store.dispatch('tags/publishContactTags', {
        contactId: this.profileId,
        tags,
      });
    },

    async changeTags(tagId) {
      if (this.contactTags[tagId] === true) {
        const tagSet = this.contact.tags.map((t) => ({ id: t.id }));
        if (!tagSet.find((t) => t.id === tagId)) {
          tagSet.push({ id: tagId });
        }
        const uniqueTagSet = Array.from(
          new Map(tagSet.map((t) => [t.id, t])).values()
        );
        try {
          await contactServices.patchContact(this.profileId, {
            tags: uniqueTagSet,
          });
        } catch (e) {
          const details = processError(e.response);
          this.$message({
            type: 'error',
            message: 'Error adding Tag: ' + details,
            showClose: true,
          });
          return false;
        }
        this.contact.tags = uniqueTagSet;
        this.publishTags(uniqueTagSet);
        this.$emit('update:add-tag', { id: tagId, contactId: this.profileId });
        this.$message({
          message: 'Successfully added tag',
          type: 'success',
        });
      } else if (this.contactTags[tagId] === false) {
        const tagSet = this.contact.tags
          .filter((t) => t.id != tagId)
          .map((t) => ({ id: t.id }));
        const uniqueTagSet = Array.from(
          new Map(tagSet.map((t) => [t.id, t])).values()
        );
        try {
          await contactServices.patchContact(this.profileId, {
            tags: uniqueTagSet,
          });
        } catch (e) {
          const details = processError(e.response);
          this.$message({
            type: 'error',
            message: 'Error removing Tag: ' + details,
            showClose: true,
          });
          return false;
        }
        this.contact.tags = uniqueTagSet;
        this.publishTags(uniqueTagSet);
        this.$emit('update:remove-tag', {
          id: tagId,
          contactId: this.profileId,
        });
        this.$message({
          message: 'Successfully removed tag',
          type: 'success',
        });
      }
    },
    async onLoad() {
      this.loading = true;
      // We do want to be sure our load and patch are in the same sequence or
      // last update will win. If two people push two changes it will break
      // Need to implement revision on the patch
      const contactData = await contactServices.fetchContactById(
        this.profileId
      );
      this.contact = contactData.data;
      const tmp = {};
      for (const tag of this.getTags) {
        tmp[+tag.id] = false;
      }
      for (const inc in this.contact.tags) {
        const tagId = this.contact.tags[inc].id;
        tmp[+tagId] = true;
      }
      this.contactTags = Object.assign({}, tmp);
      this.loading = false;
    },
  },
};
</script>
